import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Shield, LayoutDashboard, CreditCard, Building, Users, Ticket, FileText, Menu, LogOut, X, Globe, ShieldCheck, Mail, FolderKanban } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const getBackendUrl = () => {
  if (window.location.hostname !== 'localhost') {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || '';
};

function DashboardLayout({ children, title }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const backendUrl = getBackendUrl();
        const response = await fetch(`${backendUrl}/api/auth/me`, {
          credentials: 'include'
        });
        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
        }
      } catch (error) {
        console.error('Failed to fetch user:', error);
      }
    };
    fetchUser();
  }, []);

  const handleLogout = async () => {
    try {
      const backendUrl = getBackendUrl();
      await fetch(`${backendUrl}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      toast.success('Logged out successfully');
      navigate('/login');
    } catch (error) {
      toast.error('Logout failed');
    }
  };

  const navigation = [
    { name: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
    { name: 'My Subscription', icon: CreditCard, path: '/subscription' },
    { name: 'Billing', icon: CreditCard, path: '/billing' },
    { name: 'Security Status', icon: Shield, path: '/security-status' },
    { name: 'Dark Web Monitoring', icon: Globe, path: '/dark-web-monitoring' },
    { name: 'Vulnerability Scan', icon: ShieldCheck, path: '/vulnerability-scanning' },
    { name: 'Email Security', icon: Mail, path: '/email-security' },
    { name: 'Company Details', icon: Building, path: '/company' },
    { name: 'Contacts', icon: Users, path: '/contacts' },
    { name: 'Support', icon: Ticket, path: '/support' },
    { name: 'Projects', icon: FolderKanban, path: '/projects' },
    { name: 'Resources', icon: FileText, path: '/resources' },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar - Desktop */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col border-r border-border bg-card/50 backdrop-blur-xl">
        <div className="flex h-16 items-center gap-2 border-b border-border px-6">
          <div className="flex items-center">
            <span className="text-2xl font-bold" style={{ color: '#00F8B3' }}>x</span>
            <span className="text-2xl font-bold text-white">entra</span>
          </div>
        </div>
        
        <nav className="flex-1 space-y-1 px-3 py-4" data-testid="sidebar-nav">
          {navigation.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                className={`w-full flex items-center gap-3 px-3 py-2 text-sm font-mono rounded-md transition-colors ${
                  isActive
                    ? 'bg-primary/10 text-primary border border-primary/20'
                    : 'text-muted-foreground hover:bg-accent/10 hover:text-accent'
                }`}
              >
                <Icon className="h-4 w-4" />
                {item.name}
              </button>
            );
          })}
        </nav>
        
        {user && (
          <div className="border-t border-border p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center">
                <span className="text-primary font-mono font-bold">{user.name?.charAt(0) || 'U'}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-mono font-medium truncate" data-testid="user-name">{user.name}</p>
                <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                {user.role === 'admin' && (
                  <span className="text-xs text-secondary font-bold">ADMIN</span>
                )}
              </div>
            </div>
            {user.role === 'admin' && (
              <Button
                onClick={() => navigate('/admin/dashboard')}
                variant="outline"
                className="w-full justify-start mb-2 text-secondary border-secondary/20 hover:bg-secondary/10"
              >
                <Shield className="h-4 w-4 mr-2" />
                Admin Portal
              </Button>
            )}
            <Button
              onClick={handleLogout}
              variant="ghost"
              className="w-full justify-start text-muted-foreground hover:text-destructive"
              data-testid="logout-button"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        )}
      </aside>

      {/* Sidebar - Mobile */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm" onClick={() => setSidebarOpen(false)}></div>
          <aside className="fixed inset-y-0 left-0 w-64 bg-card border-r border-border">
            <div className="flex h-16 items-center justify-between border-b border-border px-6">
              <div className="flex items-center">
                <span className="text-2xl font-bold" style={{ color: '#00F8B3' }}>x</span>
                <span className="text-2xl font-bold text-white">entra</span>
              </div>
              <button onClick={() => setSidebarOpen(false)}>
                <X className="h-6 w-6 text-muted-foreground" />
              </button>
            </div>
            
            <nav className="flex-1 space-y-1 px-3 py-4">
              {navigation.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <button
                    key={item.path}
                    onClick={() => {
                      navigate(item.path);
                      setSidebarOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 px-3 py-2 text-sm font-mono rounded-md transition-colors ${
                      isActive
                        ? 'bg-primary/10 text-primary border border-primary/20'
                        : 'text-muted-foreground hover:bg-accent/10 hover:text-accent'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {item.name}
                  </button>
                );
              })}
            </nav>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <div className="lg:pl-64">
        <header className="sticky top-0 z-10 flex h-16 items-center gap-4 border-b border-border bg-card/50 backdrop-blur-xl px-6">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden text-muted-foreground"
            data-testid="mobile-menu-button"
          >
            <Menu className="h-6 w-6" />
          </button>
          <h1 className="text-xl font-mono font-bold" data-testid="page-title">{title}</h1>
        </header>
        
        <main className="p-6" data-testid="main-content">
          {children}
        </main>
      </div>
    </div>
  );
}

export default DashboardLayout;